import { isDefined, isNonEmpty, isNotDefined, noop } from '@sgme/fp';
import { SgDatePicker } from '@sgme/ui';
import { type ReactElement, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useFlexiForwardSelector } from '@/App/tiles/FlexiForward/FlexiForwardContext.ts';
import { ConditionalWrapper } from '@/App/tiles/FlexiForward/FlexiForwardTile/common/ConditionalWrapper.tsx';
import { FlexiForwardFormField } from '@/App/tiles/FlexiForward/FlexiForwardTile/common/form/FlexiForwardFormField.tsx';
import { HedgeWarningTooltipWrapper } from '@/App/tiles/FlexiForward/FlexiForwardTile/FlexiForwardHedgesForm/HedgeWarningTooltipWrapper.tsx';
import { PinnedExtra } from '@/App/tiles/FlexiForward/FlexiForwardTile/FlexiForwardHedgesForm/PinnedExtra.tsx';
import { SgAmountInput } from '@/components/sg-design-system/components/input/SgAmountInput';
import { TradeCaptureValidationWrapper } from '@/components/ui/TradeCaptureFieldErrorWrapper.tsx';
import { getFlexiForwardQuote } from '@/store/flexi-forward/helpers/rfs.ts';
import { useFlexiForwardPropertyChangedHandler } from '@/store/flexi-forward/hooks/useFlexiForwardPropertyChangedHandler.ts';
import type { Currency } from '@/types/currency.ts';

export function HedgeFormGroup({ tileId }: { tileId: string }): ReactElement {
  const { formatMessage } = useIntl();

  const {
    hedgeType: selectedHedgeType,
    displayedSpotHedge,
    isPinned = false,
    tradeCapture,

    deltaHedgeAmount: selectedDeltaHedgeValue,
    hedgeCurrencyIndex: selectedHedgeCurrencyIndex,
    notionalCurrencyIndex,
  } = useFlexiForwardSelector((flexiForward) => {
    const properties = flexiForward.properties;

    const spotHedge = properties.spotHedge;

    return {
      deltaHedgeAmount: properties.deltaHedgeAmount ?? undefined,
      hedgeCurrencyIndex: properties.deltaHedgeCurrencyIndex ?? 1,
      hedgeType: properties.hedgeType,
      isPinned: isDefined(properties.deltaHedgeAmount),
      notionalCurrencyIndex: properties.notionalCurrencyIndex,
      displayedSpotHedge: isDefined(spotHedge) ? [spotHedge] : [],
      tradeCapture: flexiForward.tradeCapture,
    };
  });

  const errors = useMemo(() => (tradeCapture.status === 'CHECKED' ? tradeCapture?.errors : undefined), [tradeCapture]);
  const warnings = useMemo(
    () => (tradeCapture.status === 'CHECKED' ? tradeCapture?.warnings : undefined),
    [tradeCapture],
  );

  const { hedgeAmount: deltaHedgeAmountFromRfs, greeksDelta } = useFlexiForwardSelector((flexiForward) => {
    const rfs = flexiForward.properties.rfs;
    const hedge = getFlexiForwardQuote(rfs)?.hedge;
    const greeks = getFlexiForwardQuote(rfs)?.greeks;

    const hedgeAmount = hedge?.notionalAmount ?? undefined;
    const greeksDelta = isDefined(greeks)
      ? Number.parseFloat(notionalCurrencyIndex === 1 ? greeks.deltaCcy1 : greeks.deltaCcy2)
      : undefined;

    return { hedgeAmount, greeksDelta };
  });

  const displayedDeltaHedgeAmount = isDefined(selectedDeltaHedgeValue)
    ? selectedDeltaHedgeValue
    : isNonEmpty(deltaHedgeAmountFromRfs)
      ? Number(deltaHedgeAmountFromRfs)
      : undefined;

  const warningAlertMessage = formatMessage({ id: 'flexiForwardTile.mainFields.deltaHedge.warning.tooltip.message' });
  const showWarningAlert = useMemo(() => {
    const isFlexiForwardValidatedHedgeAmount = true;

    if (isNotDefined(displayedDeltaHedgeAmount) || !isFlexiForwardValidatedHedgeAmount || isNotDefined(greeksDelta)) {
      return false;
    }

    const residualDelta = greeksDelta;
    const totalDelta = displayedDeltaHedgeAmount + residualDelta;
    const percentResidualDelta = Math.abs(residualDelta / totalDelta);

    const recommendedHedgeAmountInPercent = window.sgmeConfiguration.recommendedHedgeAmountInPercent;

    return percentResidualDelta > recommendedHedgeAmountInPercent;
  }, [displayedDeltaHedgeAmount, greeksDelta]);

  const inputState =
    showWarningAlert || isDefined(warnings?.deltaHedgeAmount)
      ? 'warning'
      : isDefined(errors?.deltaHedgeAmount)
        ? 'invalid'
        : 'default';

  const { hedgeCurrency, isHedgeCurrency2 }: { hedgeCurrency: Currency; isHedgeCurrency2: boolean } = useMemo(() => {
    const isHedgeCurrency2 = selectedHedgeCurrencyIndex === 2;

    return {
      hedgeCurrency: isHedgeCurrency2 ? 'USD' : 'EUR',
      isHedgeCurrency2,
    };
  }, [selectedHedgeCurrencyIndex]);

  const handleDeltaHedgeChanged = useFlexiForwardPropertyChangedHandler<number | undefined>(
    tileId,
    'Delta Hedge changed',
    (hedgeAmountValue) => {
      const deltaHedgeAmount = isDefined(hedgeAmountValue) ? hedgeAmountValue : null;
      const deltaHedgeCurrencyIndex = isDefined(hedgeAmountValue) ? selectedHedgeCurrencyIndex : 1;

      return { deltaHedgeAmount, deltaHedgeCurrencyIndex };
    },
  );

  const toggleAmountCurrency = useFlexiForwardPropertyChangedHandler<void>(
    tileId,
    'Delta Hedge amount currency changed',
    () => {
      const newHedgeCurrencyIndex = selectedHedgeCurrencyIndex === 1 ? 2 : 1;

      return { deltaHedgeAmount: selectedDeltaHedgeValue, deltaHedgeCurrencyIndex: newHedgeCurrencyIndex };
    },
  );

  return (
    <ConditionalWrapper condition={selectedHedgeType === 'Spot'}>
      <FlexiForwardFormField formattedMessageId="flexiForwardTile.mainFields.spotHedge.label">
        {/*TODO: migrate to the new SgDatePicker*/}
        <SgDatePicker
          size="sm"
          selectionType="single"
          // yearControls
          values={displayedSpotHedge}
          onChange={noop}
          isReadonly
        />
      </FlexiForwardFormField>
      <FlexiForwardFormField
        formattedMessageId="flexiForwardTile.mainFields.deltaHedge.label"
        extra={<PinnedExtra isPinned={isPinned} onClick={() => handleDeltaHedgeChanged(undefined)} />}
      >
        <HedgeWarningTooltipWrapper showWarningAlert={showWarningAlert} message={warningAlertMessage}>
          <TradeCaptureValidationWrapper errors={errors?.deltaHedgeAmount}>
            <SgAmountInput
              className={isPinned ? 'text-info fw-semibold' : undefined}
              state={inputState}
              value={displayedDeltaHedgeAmount}
              onChange={handleDeltaHedgeChanged}
              unit={hedgeCurrency}
              unitBackgroundColor={isHedgeCurrency2 ? 'warning' : undefined}
              onUnitClick={toggleAmountCurrency}
              size="sm"
              selectAllOnFocus
              data-e2e="delta-hedge-amount"
            />
          </TradeCaptureValidationWrapper>
        </HedgeWarningTooltipWrapper>
      </FlexiForwardFormField>
    </ConditionalWrapper>
  );
}
